import { NotificationType } from '@/constants';
import { AbstractModel } from '@/models/abstractModel';
import { platformDictionary } from '@/stores/simpleDictionary/platform';
import type { INotificationApi } from '@/types/notification';
import type { IPlayerSearchItem } from '@/types/player';

function splitIds(raw: string | null | undefined): string[] {
    if (!raw?.trim()) return [];
    return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}

function splitNumericIds(raw: string | null | undefined): number[] {
    return splitIds(raw)
        .map(Number)
        .filter((id) => !Number.isNaN(id));
}

export class NotificationItem extends AbstractModel {
    public countryIds: number[] = [];
    public countryNames: string[] = [];
    public createdAt: string = '';
    public id: number = 0;
    public isActive: 0 | 1 = 0;
    public isDismissed: 0 | 1 = 0;
    public platformIds: number[] = [];
    public textEn: string = '';
    public textPt: string = '';
    public toolUserIds: number[] = [];
    public toolUserNames: string[] = [];
    public typeId: number | null = null;
    public userName: string | null = null;

    constructor(data?: INotificationApi) {
        super();

        if (!data) {
            return;
        }

        this.id = data.id;
        this.createdAt = data.created_at;
        this.typeId = data.type_id;
        this.textEn = data.text_en;
        this.textPt = data.text_pt;
        this.isActive = data.is_active;
        this.isDismissed = data.is_dismissed;
        this.userName = data.user_name;
        this.platformIds = splitNumericIds(data.platform_ids);
        this.toolUserIds = splitNumericIds(data.tool_user_ids);
        this.toolUserNames = splitIds(data.tool_user_names);
        this.countryIds = splitNumericIds(data.country_ids);
        this.countryNames = splitIds(data.country_names);
    }

    static createEmpty(): NotificationItem {
        return new NotificationItem();
    }

    static typeClassFor(typeId: number | null): string {
        if (typeId == null) return '';

        switch (typeId) {
            case NotificationType.Message:
                return 'text-tbBlue';
            case NotificationType.Info:
                return 'text-infoBlue';
            case NotificationType.Warning:
                return 'text-orange';
            case NotificationType.Error:
                return 'text-tbRed';
            default:
                return '';
        }
    }

    get countriesDisplay(): string {
        if (!this.countryIds.length && !this.countryNames.length) return 'All';
        if (this.countryNames.length) return this.countryNames.join(', ');
        return this.countryIds.map(String).join(', ');
    }

    get isCountriesAll(): boolean {
        return !this.countryIds.length && !this.countryNames.length;
    }

    get isPlatformAll(): boolean {
        return !this.platformIds.length;
    }

    get isRecipientsAll(): boolean {
        return !this.toolUserIds.length && !this.toolUserNames.length;
    }

    get platformDisplay(): string {
        if (!this.platformIds.length) return 'All';
        return this.platformIds.map((id) => platformDictionary.nameById(id) || String(id)).join(', ');
    }

    get recipientsDisplay(): string {
        if (!this.toolUserIds.length && !this.toolUserNames.length) return 'All';
        if (this.toolUserNames.length) return this.toolUserNames.join(', ');
        return this.toolUserIds.map(String).join(', ');
    }

    get typeClass(): string {
        return NotificationItem.typeClassFor(this.typeId);
    }

    toApiData(): Record<string, unknown> {
        const row: Record<string, unknown> = {
            type_id: this.typeId,
            text_en: this.textEn.trim(),
            text_pt: this.textPt.trim(),
            is_active: this.isActive,
        };

        if (this.id > 0) {
            row.id = this.id;
        }

        row.platform_ids = [...this.platformIds];
        row.tool_user_ids = [...this.toolUserIds];
        row.country_ids = [...this.countryIds];

        return row;
    }

    toRecipientSearchItems(): IPlayerSearchItem[] {
        const count = Math.max(this.toolUserIds.length, this.toolUserNames.length);
        return Array.from({ length: count }, (_, index) => ({
            id: this.toolUserIds[index] ?? 0,
            login: this.toolUserNames[index] ?? String(this.toolUserIds[index] ?? ''),
            name: this.toolUserNames[index] ?? '',
            platform_id: 0,
            currency_id: 0,
        }));
    }
}
